import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/toast';
import { getUser, updateUser } from '../../services/api';
import type { User } from '../../types';

const editUserSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less')
    .optional(),
  email: z.string()
    .email('Invalid email address')
    .optional(),
  role: z.enum(['user', 'admin']),
});

export function EditUser() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get orgId from navigation state
  const orgId = (location.state as any)?.orgId;

  const form = useForm<z.infer<typeof editUserSchema>>({
    resolver: zodResolver(editUserSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      role: 'user',
    },
  });

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin()) {
      setError('Access Denied: Only super administrators can edit users.');
      return;
    }

    if (!id) {
      setError('User ID is required.');
      return;
    }

    const fetchUser = async () => {
      setLoading(true);
      setError(null);

      try {
        const user = await getUser(id) as User;
        setCurrentUser(user);

        // Pre-populate form
        form.reset({
          name: user.name || '',
          email: user.email || '',
          role: user.role === 'super_admin' ? 'admin' : (user.role as 'user' | 'admin'),
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch user details');
        console.error('Fetch user error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, isSuperAdmin, form]);

  const onSubmit = async (data: z.infer<typeof editUserSchema>) => {
    if (!id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await updateUser(id, {
        name: data.name,
        email: data.email,
        role: data.role,
      });

      toast({
        title: 'User Updated',
        description: `${data.name || data.email || 'User'} has been successfully updated.`,
      });

      // Navigate back to user list, preserving orgId if available
      if (orgId) {
        navigate('/dashboard/users', { state: { orgId } });
      } else {
        navigate('/dashboard/users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
      toast({
        title: 'Update Failed',
        description: err.message || 'Failed to update user',
        variant: 'destructive',
      });
      console.error('Update user error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  // Show access denied if not super admin
  if (!isSuperAdmin()) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/dashboard/users">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </Link>
          <ThemeToggleCompact />
        </div>
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              Only super administrators can edit users.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to={orgId ? `/dashboard/users` : '/dashboard/users'} state={orgId ? { orgId } : undefined}>
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit User</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Update user account information</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {currentUser && (
        <Card>
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>
              Update the account information for {currentUser.name || currentUser.email}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        The user's email address (used for login)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormDescription>
                        The user's display name
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Organization Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        User: Can only view their profile. Organization Admin: Can manage applications for their organization.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Display current user info */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Current Information:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">User ID:</span>
                      <code className="ml-1 font-mono text-xs bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{currentUser.id}</code>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>
                      <span className="ml-1">
                        {new Date(currentUser.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Email Verified:</span>
                      <span className="ml-1 font-medium">
                        {currentUser.email_verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Locked:</span>
                      <span className="ml-1 font-medium">
                        {currentUser.locked ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {currentUser.org_id && (
                      <div className="col-span-2">
                        <span className="text-gray-500 dark:text-gray-400">Organization ID:</span>
                        <code className="ml-1 font-mono text-xs bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{currentUser.org_id}</code>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {isSubmitting ? 'Updating...' : 'Update User'}
                  </Button>
                  <Link to={orgId ? `/dashboard/users` : '/dashboard/users'} state={orgId ? { orgId } : undefined}>
                    <Button variant="outline" type="button">
                      Cancel
                    </Button>
                  </Link>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
